/**
 * The camera turn as the renderer sees it: the angle from `CameraTurn` (render/cameraTurn.ts) put
 * on the IsoCamera each frame, the camera kept on the player as the world swings round it, and a
 * short cross-fade from the last frame whenever the quarter turn the art is painted for changes
 * (the ground, the buildings and the trees are painted per quarter turn; in between, the ground
 * plane is turned as a whole and everything standing is moved upright to its turned place).
 *
 * `?camera=free` (a prototype) lets the camera rest at any angle; `?camera=8` at every 45°.
 */
import { CameraTurn } from './cameraTurn';
import type { IsoCamera } from './camera';

/** Seconds the old quarter turn's picture takes to fade out. */
const FADE_TIME = 0.2;

/** The camera mode asked for in the page's address (`?camera=free` or `?camera=8`). */
function cameraMode(): { free: boolean; stops: number } {
  try {
    const mode =
      new URLSearchParams(location.search).get('camera') ?? localStorage.getItem('nine-swords.cameraMode');
    // C0-ROTATE: free by default (rests wherever it is let go); `?camera=4` or `?camera=8` snap.
    return { free: mode !== '4' && mode !== '8', stops: mode === '8' ? 2 : 1 };
  } catch {
    return { free: true, stops: 1 };
  }
}

export class TurnView {
  readonly control: CameraTurn;
  /** The angle put on the camera last frame (quarter turns, unwrapped). */
  private shown = 0;
  /** The last frame's picture fading out, the angle and offsets it was drawn at. */
  private fade: {
    img: HTMLCanvasElement;
    t: number;
    angle: number;
    ox: number;
    oy: number;
    w: number;
    h: number;
  } | null = null;
  /** Called whenever the angle on screen changes (the minimap turns with it). */
  onAngle: ((angle: number) => void) | null = null;

  constructor() {
    const mode = cameraMode();
    this.control = new CameraTurn(mode.free, mode.stops);
  }

  /** The angle on screen (quarter turns, unwrapped). */
  get angle(): number {
    return this.shown;
  }

  /**
   * Steps the turn and puts it on the camera, turning `cam` (world pixels, the camera's look-at)
   * with the world so the player stays in the middle. A cutscene (`staged`) is always seen from
   * the first quarter turn. When the painted quarter turn changes, `keep` is asked for the last
   * frame's picture, drawn at offsets `prev`, to fade out of. Returns true if the quarter turn changed.
   */
  update(
    dt: number,
    iso: IsoCamera,
    cam: { x: number; y: number },
    staged: boolean,
    keep: () => HTMLCanvasElement | null,
    prev: { ox: number; oy: number; w: number; h: number },
  ): boolean {
    this.control.step(dt);
    const want = staged ? Math.round(this.control.angle / 4) * 4 : this.control.angle;
    if (this.fade) {
      this.fade.t += dt;
      if (this.fade.t >= FADE_TIME) this.fade = null;
    }
    if (want === this.shown && iso.angle === iso.wrap(want)) return false;
    const rot = iso.rot;
    const [a, b, c, d] = iso.turnPx((want - this.shown) * (Math.PI / 2));
    const [cx, cy] = iso.centre();
    const [x, y] = [cam.x - cx, cam.y - cy];
    cam.x = cx + a * x + c * y;
    cam.y = cy + b * x + d * y;
    const from = this.shown;
    this.shown = want;
    iso.setAngle(want);
    this.onAngle?.(want);
    if (iso.rot === rot) return false;
    const img = keep();
    this.fade = img ? { img, t: 0, angle: from, ...prev } : null;
    return true;
  }

  /**
   * Over the world picture (art pixels, offsets `ox`, `oy`, size `w`×`h`): the last quarter turn's
   * picture fading out, turned on with the ground since it was taken so the two stay together.
   */
  drawFade(g: CanvasRenderingContext2D, iso: IsoCamera, ox: number, oy: number, w: number, h: number): void {
    const f = this.fade;
    if (!f || f.w !== w || f.h !== h) return;
    const k = f.t / FADE_TIME;
    const [a, b, c, d] = iso.spinMatrix((this.shown - f.angle) * (Math.PI / 2));
    const [cx, cy] = iso.centre();
    // A point drawn at q then (world pixel q − old offset) is now at M(q − old − C) + C + new.
    const [px, py] = [f.ox + cx, f.oy + cy];
    g.save();
    g.globalAlpha = 1 - k * k;
    g.transform(a, b, c, d, cx + ox - (a * px + c * py), cy + oy - (b * px + d * py));
    g.drawImage(f.img, 0, 0, w, h);
    g.restore();
  }

  /**
   * Turns to an angle at once, with no fade (loading the saved angle): the nearest rest angle, or
   * exactly `quarters` (`exact`, as a drag shows it; for screenshots).
   */
  jump(quarters: number, iso: IsoCamera, exact = false): void {
    this.control.set(exact ? quarters : this.control.rest(quarters));
    this.shown = this.control.angle;
    iso.setAngle(this.shown);
    this.fade = null;
    this.onAngle?.(this.shown);
  }

  /** Forgets the fade (the view jumped: a door, a zone). */
  cut(): void {
    this.fade = null;
  }
}
